import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

import {
    clampBreakpointValue,
    isBreakpointInRange,
    sanitizeKpiBreakpoints,
    type KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { MAX_CUSTOM_BREAKPOINTS } from 'constants/ppa';
import {
    handleKpiIntegerInputKeyDown,
    parseKpiIntegerInput,
} from 'components/molecules/PackArchitecture/kpiRangeSlider.utils';

const BREAKPOINT_SCROLL_STEP_PX = 76;

type BreakpointScrollState = {
    hasOverflow: boolean;
    canScrollLeft: boolean;
    canScrollRight: boolean;
};

type UseKpiAxisConfigurationBreakpointsParams = {
    min: number;
    max: number;
    breakpoints: number[];
    dataRange: KpiDataRange;
    onBreakpointsChange: (breakpoints: number[]) => void;
    onRevertToUniform: () => void;
};

export const useKpiAxisConfigurationBreakpoints = ({
    min,
    max,
    breakpoints,
    dataRange,
    onBreakpointsChange,
    onRevertToUniform,
}: UseKpiAxisConfigurationBreakpointsParams) => {
    const sortedBreakpoints = [...breakpoints].sort((left, right) => left - right);
    const [isDraftOpen, setIsDraftOpen] = useState(false);
    const [draftInputValue, setDraftInputValue] = useState('');
    const [draftInvalid, setDraftInvalid] = useState(false);
    const isAtMaxBreakpoints = sortedBreakpoints.length >= MAX_CUSTOM_BREAKPOINTS;
    const canAddBreakpoint = !isAtMaxBreakpoints && !isDraftOpen;
    const [invalidIndex, setInvalidIndex] = useState<number | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [scrollState, setScrollState] = useState<BreakpointScrollState>({
        hasOverflow: false,
        canScrollLeft: false,
        canScrollRight: false,
    });

    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const hasOverflow = container.scrollWidth > container.clientWidth + 1;
        setScrollState({
            hasOverflow,
            canScrollLeft: container.scrollLeft > 0,
            canScrollRight: container.scrollLeft + container.clientWidth < container.scrollWidth - 1,
        });
    }, []);

    useEffect(() => {
        updateScrollState();
    }, [sortedBreakpoints.length, updateScrollState]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return undefined;

        container.addEventListener('scroll', updateScrollState, { passive: true });
        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(container);

        return () => {
            container.removeEventListener('scroll', updateScrollState);
            resizeObserver.disconnect();
        };
    }, [updateScrollState]);

    useEffect(() => {
        if (invalidIndex != null && invalidIndex >= sortedBreakpoints.length) {
            setInvalidIndex(null);
        }
    }, [invalidIndex, sortedBreakpoints.length]);

    useEffect(() => {
        setInvalidIndex(null);
        setIsDraftOpen(false);
        setDraftInputValue('');
        setDraftInvalid(false);
    }, [min, max]);

    useEffect(() => {
        if (breakpoints.length === 0) {
            setIsDraftOpen(true);
        }
    }, [breakpoints.length]);

    const applyBreakpoints = (nextBreakpoints: number[]) => {
        onBreakpointsChange(sanitizeKpiBreakpoints(nextBreakpoints, min, max, dataRange));
    };

    const handleScrollBreakpoints = (direction: 'left' | 'right') => {
        scrollContainerRef.current?.scrollBy({
            left: direction === 'left' ? -BREAKPOINT_SCROLL_STEP_PX : BREAKPOINT_SCROLL_STEP_PX,
            behavior: 'smooth',
        });
    };

    const handleRemoveBreakpoint = (index: number) => {
        const next = sortedBreakpoints.filter((_, currentIndex) => currentIndex !== index);
        if (next.length === 0) {
            setInvalidIndex(null);
            onRevertToUniform();
            return;
        }

        setInvalidIndex((current) => {
            if (current == null) return null;
            if (current === index) return null;
            return current > index ? current - 1 : current;
        });
        applyBreakpoints(next);
    };

    const handleAddBreakpoint = () => {
        if (!canAddBreakpoint) {
            return;
        }

        setDraftInputValue('');
        setDraftInvalid(false);
        setInvalidIndex(null);
        setIsDraftOpen(true);
    };

    const handleCancelDraft = () => {
        setIsDraftOpen(false);
        setDraftInputValue('');
        setDraftInvalid(false);
    };

    const commitDraftBreakpoint = () => {
        const parsed = parseKpiIntegerInput(draftInputValue);
        if (parsed == null) {
            setIsDraftOpen(false);
            setDraftInputValue('');
            setDraftInvalid(false);
            return;
        }

        if (!isBreakpointInRange(parsed, min, max)) {
            setDraftInvalid(true);
            return;
        }

        setDraftInvalid(false);
        applyBreakpoints([...sortedBreakpoints, parsed]);
        setDraftInputValue('');
        setIsDraftOpen(false);
    };

    const handleDraftInputChange = (value: number | string | null) => {
        if (value == null || value === '') {
            setDraftInputValue('');
            setDraftInvalid(false);
            return;
        }

        setDraftInputValue(String(value));
        setDraftInvalid(false);
    };

    const handleDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        handleKpiIntegerInputKeyDown(event);
        if (event.key === 'Enter') {
            event.preventDefault();
            commitDraftBreakpoint();
        }
    };

    const handleDraftBlur = () => {
        if (!isDraftOpen || draftInvalid) {
            return;
        }

        const parsed = parseKpiIntegerInput(draftInputValue);
        if (parsed == null) {
            setIsDraftOpen(false);
            setDraftInputValue('');
            return;
        }

        commitDraftBreakpoint();
    };

    const handleBreakpointChange = (index: number, value: number | string | null) => {
        const parsed = parseKpiIntegerInput(value);
        if (parsed == null) {
            return;
        }

        if (!isBreakpointInRange(parsed, min, max)) {
            setInvalidIndex(index);
            return;
        }

        setInvalidIndex(null);
        const clamped = clampBreakpointValue(parsed, index, min, max, sortedBreakpoints);
        const next = sortedBreakpoints.map((breakpoint, currentIndex) =>
            currentIndex === index ? clamped : breakpoint,
        );
        applyBreakpoints(next);
    };

    const hasError = invalidIndex != null || draftInvalid;

    return {
        sortedBreakpoints,
        isDraftOpen,
        draftInputValue,
        draftInvalid,
        isAtMaxBreakpoints,
        canAddBreakpoint,
        invalidIndex,
        scrollContainerRef,
        scrollState,
        hasError,
        handleScrollBreakpoints,
        handleRemoveBreakpoint,
        handleAddBreakpoint,
        handleCancelDraft,
        handleDraftInputChange,
        handleDraftKeyDown,
        handleDraftBlur,
        handleBreakpointChange,
    };
};
